import Box from "@mui/material/Box";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import type { ReadOnlyFieldsetProps } from "../types";

const ink = tokens.colour.neutral.ink;

/**
 * ADR-20261102-h2fd (AC-11): H2's read-only pattern (`portal-readonly.css`). Every control is
 * really disabled by the native fieldset; the styling keeps the question, help, selected answer and
 * entered text at full ink, fades only unselected options to 50%, and names the state in words
 * with the legend "Read only".
 */
export function ReadOnlyFieldset({ children, testId }: Readonly<ReadOnlyFieldsetProps>) {
  return (
    <Box
      component="fieldset"
      disabled
      {...cleanProps({ "data-testid": testId })}
      data-portal-readonly="true"
      sx={{
        background: "none",
        border: 0,
        margin: 0,
        padding: 0,
        minWidth: 0,
        // H2: the read-only fieldset is a column, 16px between the legend and each question.
        display: "flex",
        flexDirection: "column",
        gap: `${tokens.spacing.md}px`,
        cursor: "not-allowed",
        "& *": { cursor: "not-allowed !important" },
        "& .Mui-disabled, & .MuiFormLabel-root.Mui-disabled, & .MuiFormControlLabel-label.Mui-disabled, & .MuiFormHelperText-root.Mui-disabled": {
          color: `${ink} !important`,
        },
        "& label:has(input:checked) .MuiRadio-root, & label:has(input:checked) .MuiCheckbox-root": {
          color: `${tokens.colour.brand[700]} !important`,
        },
        '& label:has(input[type="radio"]:not(:checked)), & label:has(input[type="checkbox"]:not(:checked))': { opacity: 0.5 },
        "& .MuiInputBase-root, & input, & textarea": {
          background: `${tokens.colour.neutral.surface} !important`,
          color: `${ink} !important`,
          WebkitTextFillColor: `${ink} !important`,
        },
        "& button": { pointerEvents: "none" },
      }}
    >
      <Box
        component="legend"
        sx={{ float: "left", width: "100%", padding: 0, margin: 0, fontSize: 15, lineHeight: 1.4, fontWeight: tokens.typography.weights.bold, color: tokens.colour.brand[700] }}
      >
        Read only
      </Box>
      {children}
    </Box>
  );
}
